/**
 * Custom Design - theme ENGINE.
 *
 * Turns a custom-theme token object into:
 *   1. Base CSS variables (--v-theme-*) for the active mode (light/dark), so
 *      every Vuetify utility (bg-primary, text-accent, ...) follows.
 *   2. A generated, high-specificity stylesheet (injected as <style>) that
 *      restyles each component SLOT (sidebar, header, buttons, cards, ...).
 *
 * The slot stylesheet uses !important so it wins over branding/personal theme,
 * which means a custom theme fully overrides whatever was applied before.
 */
import { SLOTS } from './slotsCatalog'

const STYLE_ID = 'custom-theme-css'

function hexToRgb(hex) {
  if (!hex) return null
  const m = String(hex).trim().replace('#', '')
  if (m.length !== 6) return null
  return `${parseInt(m.slice(0, 2), 16)},${parseInt(m.slice(2, 4), 16)},${parseInt(m.slice(4, 6), 16)}`
}

export function emptyTokens(base = null) {
  return { version: 1, base: base || { light: {}, dark: {} }, slots: {} }
}

/** Build a CSS background value from a fill descriptor. */
function fillToBackground(fill) {
  if (!fill || !fill.type || fill.type === 'none') return null
  if (fill.type === 'solid') return fill.color || null
  if (fill.type === 'gradient') {
    const a = (fill.colors && fill.colors[0]) || fill.color || '#000000'
    const b = (fill.colors && fill.colors[1]) || fill.color || '#ffffff'
    const angle = Number.isFinite(fill.angle) ? fill.angle : 135
    return `linear-gradient(${angle}deg, ${a}, ${b})`
  }
  return null
}

// App components (e.g. the top bar `.app-top-bar`) carry their own
// high-specificity `!important` rules, so a bare `.v-app-bar` rule loses the
// cascade. Scoping every slot selector under `html[data-custom-theme]` raises
// specificity (and only matches while a custom theme is active) so our rules win.
const SCOPE = 'html[data-custom-theme] '
function scopeSelector(selectorList) {
  return selectorList
    .split(',')
    .map((s) => SCOPE + s.trim())
    .join(', ')
}

function cssForSlot(slot, data) {
  if (!data) return ''
  const sel = scopeSelector(slot.target || slot.match)
  const out = []

  if (slot.props.includes('fill') && data.fill) {
    const bg = fillToBackground(data.fill)
    if (bg) out.push(`${sel}{background:${bg} !important;}`)
    const skin = data.fill.skin
    if (skin && skin.url) {
      out.push(
        `${sel}{background-image:url("${skin.url}") !important;` +
        `background-size:${skin.size || 'cover'} !important;` +
        `background-position:center !important;` +
        `background-repeat:${skin.size === 'repeat' ? 'repeat' : 'no-repeat'} !important;` +
        `background-blend-mode:${skin.blend || 'normal'};}`,
      )
    }
  }
  if (slot.props.includes('text') && data.text) {
    out.push(`${sel}{color:${data.text} !important;}`)
  }
  return out.join('\n')
}

/** Generate the full slot stylesheet for the given mode. */
export function buildSlotCss(tokens, mode) {
  if (!tokens || !tokens.slots) return ''
  const blocks = []
  for (const slot of SLOTS) {
    const data = tokens.slots[slot.id]
    if (!data) continue
    const modeData = data[mode] || data.light || data
    const css = cssForSlot(slot, modeData)
    if (css) blocks.push(`/* ${slot.id} */\n${css}`)
  }
  return blocks.join('\n\n')
}

const BASE_VAR_MAP = {
  primary: '--v-theme-primary',
  accent: '--v-theme-accent',
  background: '--v-theme-background',
  surface: '--v-theme-surface',
  success: '--v-theme-success',
  warning: '--v-theme-warning',
  error: '--v-theme-error',
  info: '--v-theme-info',
}

function applyBaseVars(base, mode) {
  const root = document.documentElement
  const b = (base && (base[mode] || base.light)) || {}
  for (const [key, varName] of Object.entries(BASE_VAR_MAP)) {
    const rgb = hexToRgb(b[key])
    if (rgb) root.style.setProperty(varName, rgb)
  }
  const tp = hexToRgb(b.text_primary)
  if (tp) {
    root.style.setProperty('--v-theme-on-surface', tp)
    root.style.setProperty('--v-theme-on-background', tp)
    root.style.setProperty('--brand-text-primary', b.text_primary)
  }
  if (b.text_secondary) root.style.setProperty('--brand-text-secondary', b.text_secondary)
}

/** Apply a custom theme (base vars + slot stylesheet) for the active mode. */
export function applyCustomTheme(tokens, mode = 'light') {
  if (!tokens) return
  applyBaseVars(tokens.base, mode)
  let el = document.getElementById(STYLE_ID)
  if (!el) {
    el = document.createElement('style')
    el.id = STYLE_ID
    document.head.appendChild(el)
  }
  el.textContent = buildSlotCss(tokens, mode)
  document.documentElement.dataset.customTheme = '1'
}

/** Remove the slot stylesheet. Base vars are reasserted by branding.applyToDom,
 *  which the caller should run right after to restore the domain/personal look. */
export function clearCustomTheme() {
  const el = document.getElementById(STYLE_ID)
  if (el) el.textContent = ''
  delete document.documentElement.dataset.customTheme
}
